import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Package,
  Flame,
  Heart,
  TrendingUp,
  Clock,
  Star,
  X,
  Plus,
  Search,
} from 'lucide-react';
import { Cigar, CIGARS } from '../data/cigars';
import {
  TasteProfile,
  getPersonalizedRecommendations,
  getProfileInsights,
  PersonalizedRecommendation,
} from '../data/personalization';
import { CigarDetailModal } from '../components/DailyRitual';

interface MyHumidorViewProps {
  savedCigarIds: string[];
  smokedCigarIds: string[];
  tasteProfile: TasteProfile;
  onRemoveCigar: (cigarId: string) => void;
  onAddCigar: (cigar: Cigar) => void;
  onLogSmoke: (cigar: Cigar) => void;
  onBack: () => void;
}

type ViewMode = 'humidor' | 'recommendations' | 'profile';

export const MyHumidorView: React.FC<MyHumidorViewProps> = ({
  savedCigarIds,
  smokedCigarIds,
  tasteProfile,
  onRemoveCigar,
  onAddCigar,
  onLogSmoke,
  onBack,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('humidor');
  const [selectedCigar, setSelectedCigar] = useState<Cigar | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'strength' | 'recent'>('name');

  // Get saved cigars from database
  const savedCigars = useMemo(() => {
    return CIGARS.filter(c => savedCigarIds.includes(c.id));
  }, [savedCigarIds]);

  // Filter cigars based on search
  const filteredCigars = useMemo(() => {
    let result = savedCigars;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        c =>
          c.brand.toLowerCase().includes(query) ||
          c.line.toLowerCase().includes(query) ||
          c.primaryFlavors.some(f => f.toLowerCase().includes(query))
      );
    }

    // Sort
    return result.sort((a, b) => {
      switch (sortBy) {
        case 'strength':
          const strengths = ['mild', 'mild-medium', 'medium', 'medium-full', 'full'];
          return strengths.indexOf(a.strength) - strengths.indexOf(b.strength);
        case 'recent':
          return savedCigarIds.indexOf(b.id) - savedCigarIds.indexOf(a.id);
        default:
          return a.brand.localeCompare(b.brand);
      }
    });
  }, [savedCigars, searchQuery, sortBy, savedCigarIds]);

  // Get recommendations
  const recommendations = useMemo(() => {
    return getPersonalizedRecommendations(tasteProfile, savedCigarIds, smokedCigarIds, 8);
  }, [tasteProfile, savedCigarIds, smokedCigarIds]);

  // Get profile insights
  const insights = useMemo(() => {
    return getProfileInsights(tasteProfile);
  }, [tasteProfile]);

  // Available cigars to add (not already saved)
  const availableCigars = useMemo(() => {
    return CIGARS.filter(c => !savedCigarIds.includes(c.id));
  }, [savedCigarIds]);

  const strengthColor = (strength: string) => {
    switch (strength) {
      case 'mild':
        return 'bg-green-500/20 text-green-400';
      case 'mild-medium':
        return 'bg-lime-500/20 text-lime-400';
      case 'medium':
        return 'bg-amber-500/20 text-amber-400';
      case 'medium-full':
        return 'bg-orange-500/20 text-orange-400';
      case 'full':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-stone-500/20 text-stone-400';
    }
  };

  return (
    <div className="min-h-screen bg-stone-900 text-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-stone-900/95 backdrop-blur border-b border-stone-800">
        <div className="flex items-center justify-between p-4">
          <button onClick={onBack} className="p-2 -ml-2 touch-manipulation">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold">My Humidor</h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="p-2 -mr-2 text-amber-500 touch-manipulation"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-stone-800">
          <button
            onClick={() => setViewMode('humidor')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              viewMode === 'humidor'
                ? 'text-amber-500 border-b-2 border-amber-500'
                : 'text-stone-400'
            }`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            Saved ({savedCigars.length})
          </button>
          <button
            onClick={() => setViewMode('recommendations')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              viewMode === 'recommendations'
                ? 'text-amber-500 border-b-2 border-amber-500'
                : 'text-stone-400'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-2" />
            For You
          </button>
          <button
            onClick={() => setViewMode('profile')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              viewMode === 'profile'
                ? 'text-amber-500 border-b-2 border-amber-500'
                : 'text-stone-400'
            }`}
          >
            <Heart className="w-4 h-4 inline mr-2" />
            Profile
          </button>
        </div>
      </div>

      <div className="p-4">
        {viewMode === 'humidor' && (
          <>
            {/* Search and Sort */}
            {savedCigars.length > 0 && (
              <div className="flex gap-2 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                  <input
                    type="text"
                    placeholder="Search your cigars..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:border-amber-500"
                  />
                </div>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as typeof sortBy)}
                  className="px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="name">A-Z</option>
                  <option value="strength">Strength</option>
                  <option value="recent">Recent</option>
                </select>
              </div>
            )}

            {/* Empty State */}
            {savedCigars.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-16 h-16 mx-auto text-stone-600 mb-4" />
                <h2 className="text-lg font-medium mb-2">Your Humidor is Empty</h2>
                <p className="text-stone-400 mb-6">
                  Save cigars from the Explorer or recommendations to build your collection
                </p>
                <button
                  onClick={() => setViewMode('recommendations')}
                  className="px-6 py-3 bg-amber-600 hover:bg-amber-500 rounded-lg font-medium transition-colors"
                >
                  View Recommendations
                </button>
              </div>
            )}

            {/* Saved Cigars Grid */}
            {filteredCigars.length > 0 && (
              <div className="space-y-3">
                {filteredCigars.map(cigar => (
                  <div
                    key={cigar.id}
                    className="bg-stone-800 rounded-xl p-4 border border-stone-700"
                  >
                    <div className="flex items-start justify-between">
                      <button
                        onClick={() => setSelectedCigar(cigar)}
                        className="flex-1 text-left"
                      >
                        <h3 className="font-semibold">{cigar.brand}</h3>
                        <p className="text-stone-400 text-sm">{cigar.line}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${strengthColor(
                              cigar.strength
                            )}`}
                          >
                            {cigar.strength}
                          </span>
                          <span className="text-xs text-stone-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {cigar.timeCommitment} min
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {cigar.primaryFlavors.slice(0, 3).map(flavor => (
                            <span
                              key={flavor}
                              className="text-xs px-2 py-0.5 bg-stone-700 rounded-full text-stone-300"
                            >
                              {flavor}
                            </span>
                          ))}
                        </div>
                      </button>
                      <div className="flex flex-col gap-2 ml-3">
                        <button
                          onClick={() => onLogSmoke(cigar)}
                          className="p-2 bg-amber-600/20 text-amber-500 rounded-lg hover:bg-amber-600/30 transition-colors"
                          title="Log Smoke"
                        >
                          <Flame className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => onRemoveCigar(cigar.id)}
                          className="p-2 bg-stone-700 text-stone-400 rounded-lg hover:bg-stone-600 transition-colors"
                          title="Remove"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searchQuery && filteredCigars.length === 0 && (
              <p className="text-center text-stone-400 py-8">
                No cigars match "{searchQuery}"
              </p>
            )}
          </>
        )}

        {viewMode === 'recommendations' && (
          <>
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Recommended For You</h2>
              <p className="text-stone-400 text-sm">
                Based on your taste profile
                {tasteProfile.confidence < 0.3 && (
                  <span className="text-amber-500">
                    {' '}
                    • Add more cigars to improve accuracy
                  </span>
                )}
              </p>
            </div>

            {recommendations.length > 0 ? (
              <div className="space-y-4">
                {recommendations.map((rec, index) => (
                  <RecommendationCard
                    key={rec.cigar.id}
                    recommendation={rec}
                    rank={index + 1}
                    isSaved={savedCigarIds.includes(rec.cigar.id)}
                    onViewDetails={() => setSelectedCigar(rec.cigar)}
                    onSave={() => onAddCigar(rec.cigar)}
                    strengthColor={strengthColor}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-400">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 text-stone-600" />
                <p>Add cigars to your journal to get personalized recommendations</p>
              </div>
            )}
          </>
        )}

        {viewMode === 'profile' && (
          <>
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Your Taste Profile</h2>
              <div className="flex items-center gap-2 text-sm text-stone-400">
                <span>Confidence:</span>
                <div className="flex-1 h-2 bg-stone-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all"
                    style={{ width: `${tasteProfile.confidence * 100}%` }}
                  />
                </div>
                <span>{Math.round(tasteProfile.confidence * 100)}%</span>
              </div>
            </div>

            {/* Insights */}
            <div className="space-y-3 mb-6">
              {insights.map((insight, index) => (
                <div
                  key={index}
                  className="bg-stone-800 rounded-lg p-4 border border-stone-700"
                >
                  <p className="text-stone-300">{insight}</p>
                </div>
              ))}
            </div>

            {/* Strength Preferences */}
            <div className="bg-stone-800 rounded-xl p-4 border border-stone-700 mb-4">
              <h3 className="font-medium mb-3">Strength Preferences</h3>
              <div className="space-y-2">
                {(['mild', 'mild-medium', 'medium', 'medium-full', 'full'] as const).map(
                  strength => {
                    const value = tasteProfile.strengthPreference[strength];
                    return (
                      <div key={strength} className="flex items-center gap-3">
                        <span className="text-sm text-stone-400 w-28 capitalize">
                          {strength}
                        </span>
                        <div className="flex-1 h-2 bg-stone-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              value > 0.5
                                ? 'bg-amber-500'
                                : value > 0.2
                                ? 'bg-amber-600'
                                : 'bg-stone-600'
                            }`}
                            style={{ width: `${value * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-stone-500 w-8">
                          {Math.round(value * 100)}%
                        </span>
                      </div>
                    );
                  }
                )}
              </div>
            </div>

            {/* Top Flavors */}
            {tasteProfile.topFlavors.length > 0 && (
              <div className="bg-stone-800 rounded-xl p-4 border border-stone-700 mb-4">
                <h3 className="font-medium mb-3">Favorite Flavors</h3>
                <div className="flex flex-wrap gap-2">
                  {tasteProfile.topFlavors.map((flavor, index) => (
                    <span
                      key={flavor}
                      className={`px-3 py-1.5 rounded-full text-sm ${
                        index === 0
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                          : 'bg-stone-700 text-stone-300'
                      }`}
                    >
                      {flavor}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-stone-800 rounded-lg p-3 text-center border border-stone-700">
                <div className="text-2xl font-bold text-amber-500">
                  {tasteProfile.noteCount}
                </div>
                <div className="text-xs text-stone-400">Journal Notes</div>
              </div>
              <div className="bg-stone-800 rounded-lg p-3 text-center border border-stone-700">
                <div className="text-2xl font-bold text-amber-500">
                  {tasteProfile.savedCount}
                </div>
                <div className="text-xs text-stone-400">Saved Cigars</div>
              </div>
              <div className="bg-stone-800 rounded-lg p-3 text-center border border-stone-700">
                <div className="text-2xl font-bold text-amber-500">
                  {tasteProfile.smokeLogCount}
                </div>
                <div className="text-xs text-stone-400">Smoke Log</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Cigar Detail Modal */}
      {selectedCigar && (
        <CigarDetailModal
          cigar={selectedCigar}
          onClose={() => setSelectedCigar(null)}
          isSaved={savedCigarIds.includes(selectedCigar.id)}
          onSave={() => {
            if (savedCigarIds.includes(selectedCigar.id)) {
              onRemoveCigar(selectedCigar.id);
            } else {
              onAddCigar(selectedCigar);
            }
          }}
          onLogSmoke={() => {
            onLogSmoke(selectedCigar);
            setSelectedCigar(null);
          }}
        />
      )}

      {/* Add Cigar Modal */}
      {showAddModal && (
        <AddCigarModal
          cigars={availableCigars}
          onAdd={cigar => {
            onAddCigar(cigar);
            setShowAddModal(false);
          }}
          onClose={() => setShowAddModal(false)}
          strengthColor={strengthColor}
        />
      )}
    </div>
  );
};

// Recommendation Card Component
const RecommendationCard: React.FC<{
  recommendation: PersonalizedRecommendation;
  rank: number;
  isSaved: boolean;
  onViewDetails: () => void;
  onSave: () => void;
  strengthColor: (strength: string) => string;
}> = ({ recommendation, rank, isSaved, onViewDetails, onSave, strengthColor }) => {
  const { cigar, matchScore, matchReasons } = recommendation;

  return (
    <div className="bg-stone-800 rounded-xl p-4 border border-stone-700">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 font-bold shrink-0">
          {rank}
        </div>
        <div className="flex-1">
          <button onClick={onViewDetails} className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">{cigar.brand}</h3>
              <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">
                {matchScore}% match
              </span>
            </div>
            <p className="text-stone-400 text-sm">{cigar.line}</p>
          </button>

          <div className="flex items-center gap-2 mt-2">
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${strengthColor(cigar.strength)}`}
            >
              {cigar.strength}
            </span>
            <span className="text-xs text-stone-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {cigar.timeCommitment} min
            </span>
            {cigar.rating && (
              <span className="text-xs text-stone-500 flex items-center gap-1">
                <Star className="w-3 h-3 text-amber-500" />
                {cigar.rating}
              </span>
            )}
          </div>

          {matchReasons.length > 0 && (
            <div className="mt-2 space-y-1">
              {matchReasons.map((reason, index) => (
                <p key={index} className="text-xs text-amber-500/80">
                  • {reason}
                </p>
              ))}
            </div>
          )}
        </div>

        {!isSaved && (
          <button
            onClick={onSave}
            className="p-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500 transition-colors shrink-0"
          >
            <Plus className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

// Add Cigar Modal
const AddCigarModal: React.FC<{
  cigars: Cigar[];
  onAdd: (cigar: Cigar) => void;
  onClose: () => void;
  strengthColor: (strength: string) => string;
}> = ({ cigars, onAdd, onClose, strengthColor }) => {
  const [search, setSearch] = useState('');

  const filtered = cigars.filter(c => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      c.brand.toLowerCase().includes(query) ||
      c.line.toLowerCase().includes(query)
    );
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4">
      <div className="bg-stone-900 rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden border border-stone-700">
        <div className="sticky top-0 bg-stone-900 border-b border-stone-800 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Add to Humidor</h2>
            <button onClick={onClose} className="p-1 text-stone-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
            <input
              type="text"
              placeholder="Search cigars..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:border-amber-500"
              autoFocus
            />
          </div>
        </div>

        <div className="overflow-y-auto max-h-[60vh] p-4">
          <div className="space-y-2">
            {filtered.slice(0, 20).map(cigar => (
              <button
                key={cigar.id}
                onClick={() => onAdd(cigar)}
                className="w-full text-left p-3 bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">{cigar.brand}</h3>
                    <p className="text-sm text-stone-400">{cigar.line}</p>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${strengthColor(
                      cigar.strength
                    )}`}
                  >
                    {cigar.strength}
                  </span>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-center text-stone-400 py-8">No cigars found</p>
            )}
            {filtered.length > 20 && (
              <p className="text-center text-stone-500 text-sm py-2">
                Showing first 20 results. Refine your search for more.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
